import React, { FC, ReactNode } from 'react'
import { Link as RouteLink } from 'react-router-dom'
import { Alert, AlertTitle } from '@mui/material'
import { ErrorBoundary, CustomFade } from '../'
import sucessImage from '../../images/svg/sucessImage.svg'
import errorImage from '../../images/svg/errorImage.svg'
import warningImage from '../../images/svg/warningIcon.svg'
import infoImage from '../../images/svg/infoIcon.svg'

import { routes } from 'config'

interface IProps {
  severity: any
  title?: string
  children?: ReactNode
  count?: number
  handleClick?: any
  transaction?: any
  isVisible?: boolean
  factorType?: 'sms' | 'email' | 'totp'
}

const CustomAlert: FC<IProps> = ({
  severity,
  title,
  children,
  count,
  handleClick,
  transaction,
  isVisible = true,
  factorType = 'sms',
}) => {
  if (!children) {
    return null
  }

  let icon

  switch (severity) {
    case 'error':
      icon = <img className="alignIcon" alt="error" src={errorImage} />
      break

    case 'success':
      icon = <img className="alignIcon" alt="success" src={sucessImage} />
      break

    case 'info':
      icon = <img className="alignIcon" alt="info" src={infoImage} />
      break

    case 'warning':
      icon = <img className="alignIcon" alt="warning" src={warningImage} />
      break

    default:
      icon = false
  }

  let content

  // Determine factor destination text for error messages
  let factorDestination = 'to your phone'
  if (factorType === 'email') {
    factorDestination = 'to your email'
  } else if (factorType === 'totp') {
    factorDestination = 'in your authenticator app'
  }

  switch (children) {
    case 'E0000004':
      content = (
        <>
          Sorry, that information didn't match our records. <br />
          Can we help you recover your{' '}
          <RouteLink to={routes.forgotLoginName}>login name</RouteLink> or{' '}
          <RouteLink to={routes.forgotPassword}>password</RouteLink>?
        </>
      )
      title = 'Invalid Credentials'
      break

    case 'E0000105':
      content = (
        <>
          The link has expired or has been used previously. Please return to
          login.
        </>
      )
      break

    case 'E0000068':
      content = (
        <>
          The 6-digit verification code that you entered does not match the code
          we sent {factorDestination}. Please check and try again.
          {count != null && <> You have {count} attempts remaining.</>}
          {handleClick && transaction && (
            <>
              {' '}
              <button
                className="resendBtn"
                onClick={() => {
                  handleClick(transaction)
                }}
              >
                Didn't get the code? Resend
              </button>
            </>
          )}
        </>
      )
      title = 'Verification Code Invalid'
      break

    case 'E0000069':
    case 'E0000119':
      content = (
        <>
          Your account was locked after too many failed attempts for security
          purposes. You may try again in 5 minutes. If you need assistance,
          contact Customer Care at{' '}
          <a className="anchorTags" href="mailto:support@brightmls.com">
            support@brightmls.com
          </a>{' '}
          or{' '}
          <a className="anchorTags" href="tel:1-844-552-7444">
            1-844-552-7444
          </a>
          .
        </>
      )
      title = 'Too Many Attempts'
      break

    case 'resend_success':
      content = (
        <>
          A new 6-digit verification code has been sent to 212-555-1234. It may
          take up to 5 minutes for your code to arrive.
        </>
      )
      title = 'Verification Code Sent'
      break

    case 'E0000011':
      content = (
        <>
          Your login session has expired, and you are unable to enter the
          6-digit verification code. Please attempt to log in once more.
        </>
      )
      title = 'Session Expired'
      break

    case 'E0000082':
      content = (
        <>
          You have exceeded the number of attempts with this passcode. Please{' '}
          <button
            className="resendBtn"
            onClick={() => {
              handleClick(transaction)
            }}
          >
            resend the verification code
          </button>{' '}
          to try again.
        </>
      )

      title = 'Too Many Attempt'
      break

    default:
      if (typeof children === 'string') {
        if (
          children.includes('Request failed with status code 403') ||
          children.includes('Too many attempts') ||
          children.includes('too frequently') ||
          children.includes('rate limit')
        ) {
          content = (
            <>
              Too many attempts. Please wait a few minutes before trying again.
            </>
          )
          title = 'Please Wait Before Resending'
        } else {
          content = children
        }
      } else {
        content = children
      }
      break
  }

  if (content === undefined) {
    content = children
  }

  return (
    <>
      {isVisible && (
        <ErrorBoundary>
          <CustomFade>
            <Alert severity={severity} icon={icon}>
              {title && (
                <AlertTitle style={{ fontSize: '14px', fontWeight: 600 }}>
                  {title}
                </AlertTitle>
              )}
              {content}
            </Alert>
          </CustomFade>
        </ErrorBoundary>
      )}
    </>
  )
}

export default CustomAlert
